/**
 * LexasCMS image transformations: `width` resizes (never upscales) and `format=webp` converts.
 * Editors upload PNG banners of several hundred kB; as WebP at the shown size they are ~90 % smaller.
 */

const LEXAS_ASSET_HOST = 'https://assets.lexascms.com/'
/** Animated GIFs and vector images are served as they are */
const KEEP_AS_IS = /\.(gif|svg)$/i

function isTransformable(url: string): boolean {
  return url.startsWith(LEXAS_ASSET_HOST) && !KEEP_AS_IS.test(url.split('?')[0])
}

export type LexasFormat = 'webp' | 'avif'

/** The image as WebP (or AVIF), resized to `width` if given. Other URLs are returned unchanged. */
export function lexasImage(url: string | null | undefined, width?: number, format: LexasFormat = 'webp'): string {
  if (!url) return ''
  if (!isTransformable(url)) return url
  const params = [width ? `width=${width}` : '', `format=${format}`].filter(Boolean).join('&')
  return `${url}${url.includes('?') ? '&' : '?'}${params}`
}

/**
 * srcset for the given widths. Widths at or above the original width are replaced by the original
 * (LexasCMS does not upscale). Empty for images that cannot be transformed: use `src` alone.
 */
export function lexasSrcset(url: string | null | undefined, widths: number[], originalWidth?: number, format: LexasFormat = 'webp'): string {
  if (!url || !isTransformable(url)) return ''
  const smaller = originalWidth ? widths.filter(width => width < originalWidth) : widths
  const entries = smaller.map(width => `${lexasImage(url, width, format)} ${width}w`)
  if (originalWidth) entries.push(`${lexasImage(url, undefined, format)} ${originalWidth}w`)
  return entries.join(', ')
}
